import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormArray, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';

function getFormArray(size: number): FormArray {
  const arr = [];

  for (let i = 0; i < size; i++) {
    arr.push(new FormControl('', Validators.required));
  }

  return new FormArray(arr);
}

@Component({
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: OtpInputComponent,
    multi: true,
  },
  {
    provide: NG_VALIDATORS,
    useExisting: OtpInputComponent,
    multi: true,
  },]
})
export class OtpInputComponent implements ControlValueAccessor {
  @ViewChildren('inputEl') inputEls!: QueryList<ElementRef<HTMLInputElement>>;
  #scheduledFocus: number | null = null;

  @Output() otpFormSubmitEvent = new EventEmitter<string>();

  @Input() set size(size: number) {
    this.inputsForm = getFormArray(size);
    this.#size = size;
  }

  #size = 4;
  inputsForm = getFormArray(this.#size);

  ngOnInit() {
    // TODO: TIM: Do we need to manually unsubscribe?
    this.inputsForm.valueChanges.subscribe(value => {
      // TODO: Tim: Add proper form validation check here
      if (this.inputsForm.valid) {
        this.submitOtpForm();
      }
    });
  }

  onChange?: (value: string) => void;
  onTouched?: () => void;

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: string): void {
    if (value?.length) {
      throw new Error('Otp input is not supposed to be prefilled with data');
    }
  
    // Reset all input values
    this.inputsForm.setValue(new Array(this.#size).fill(''));
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputsForm.disable();
    } else {
      this.inputsForm.enable();
    }
  }

  handleInput() {
    this.#updateWiredValue();
  
    if (this.#scheduledFocus != null) {
      this.#focusInput(this.#scheduledFocus);
      this.#scheduledFocus = null;
    }
  }

  handleKeyPress(e: KeyboardEvent, idx: number) {
    const isDigit = /\d/.test(e.key);
  
    // Safari fires Cmd + V through keyPress event as well
    // so we need to handle it here and let it through
    if (e.key === 'v' && e.metaKey) {
      return true;
    }
  
    if (isDigit && idx + 1 < this.#size) {
      this.#scheduledFocus = idx + 1;
    }
  
    if (isDigit && this.inputsForm.controls[idx].value) {
      this.inputsForm.controls[idx].setValue('');
    }
  
    return isDigit;
  }

  handleFocus(e: FocusEvent) {
    (e.target as HTMLInputElement).select();
  }
  
  #focusInput(idx: number) {
    setTimeout(() => this.inputEls.get(idx)?.nativeElement.focus());
  }
  
  #updateWiredValue() {
    setTimeout(() => this.onChange?.(this.inputsForm.value.join('')));
  }

  handleKeyDown(e: KeyboardEvent, idx: number) {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (idx > 0) {
        this.#scheduledFocus = idx - 1;
      }
    }
  }

  handlePaste(e: ClipboardEvent, idx: number) {
    e.preventDefault();
  
    if (idx !== 0) {
      return;
    }
  
    const pasteData = e.clipboardData?.getData('text');
    const regex = new RegExp(`\\d{${this.#size}}`);
  
    if (!pasteData || !regex.test(pasteData)) {
      return;
    }
  
    for (let i = 0; i < pasteData.length; i++) {
      this.inputsForm.controls[i].setValue(pasteData[i]);
    }
  
    this.#focusInput(this.inputEls.length - 1);
    this.#updateWiredValue();
    this.onTouched!(); // TODO: TIM: had to add a not-null operator to this call
  }

  // TODO: TIM: check how to implement validation
  validate(control: AbstractControl<string, string>): ValidationErrors | null {
    if (!control.value || control.value.length < this.#size) {
      return {
        otpInput: 'Value is incorrect',
      };
    }
  
    return null;
  }

  submitOtpForm() {
    const otpCode = this.inputsForm.value.join('');
    this.otpFormSubmitEvent.emit(otpCode);
  }

  resetOtpForm(): void {
    this.inputsForm.reset();
  }
}
